import { useState } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GoogleIcon from "@mui/icons-material/Google";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useHistory } from "react-router-dom";
import { useAppContext, useSyncStatus } from "../../utils/AppContext";
import { goBackOrHome } from "../../utils/navigation";
import FeedbackDialog from "../FeedbackDialog";

const primary = "#1535BD";

const PageHeader = ({ title, isHome, noBack, desktopBack, onWalkthroughClick }) => {
  const { userData, handleLogin, handleLogout, settings } = useAppContext();
  const syncStatus = useSyncStatus();
  const history = useHistory();

  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pt: "max(env(safe-area-inset-top, 0px), 10px)",
          pb: 1.5,
          width: "100%",
          // App chrome: a long press shouldn't select the title and pop the text toolbar.
          userSelect: "none",
          ...(!isHome && {
            position: "sticky",
            top: 0,
            zIndex: 100,
            background: "#F1F3F4",
            mx: -2,
            px: 2,
            width: "calc(100% + 32px)",
          }),
        }}
      >
        {/* Left: hamburger (home), back button, or nothing */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          {isHome ? (
            <IconButton
              id="wt-menu-btn"
              size="small"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              // A 48dp touch target with the icon and the row's layout where the small
              // button had them: the extra padding is taken back by negative margins,
              // into the header's top padding and the page's side gutter.
              sx={{ color: primary, display: { xs: "flex", sm: "none" }, p: "12px", m: "-7px" }}
            >
              <MenuIcon />
            </IconButton>
          ) : !noBack ? (
            <IconButton
              size="small"
              aria-label="Back"
              onClick={() => goBackOrHome(history)}
              // A 48dp touch target, laid out as the menu button is.
              sx={{ color: primary, p: "12px", m: "-7px", ...(desktopBack && { display: { xs: "none", sm: "flex" } }) }}
            >
              <ArrowBackIcon />
            </IconButton>
          ) : null}

          {/* Long titles (e.g. a pasted phrase) end in an ellipsis instead of pushing the
              right-hand buttons off-screen. dir="auto" keeps the start of an Arabic title. */}
          <Typography
            noWrap
            dir="auto"
            sx={{
              fontWeight: 800,
              fontSize: "1.1rem",
              color: primary,
              letterSpacing: "-0.02em",
              fontFamily: "Mulish, sans-serif",
              // Tall enough that noWrap's overflow:hidden doesn't clip descenders or harakat,
              // while the negative margins keep the row as tall as a 1em line made it.
              lineHeight: 1.5,
              my: "-0.25em",
              ...(isHome && { display: { xs: "block", sm: "none" } }),
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Right: sync status + auth */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 80, flexShrink: 0, justifyContent: "flex-end" }}>
          {userData && syncStatus !== "idle" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.3,
                borderRadius: "12px",
                bgcolor:
                  syncStatus === "error"
                    ? "rgba(211,47,47,0.1)"
                    : syncStatus === "offline"
                    ? "rgba(120,117,133,0.12)"
                    : "rgba(21,53,189,0.08)",
                fontSize: "0.7rem",
                fontWeight: 700,
                color:
                  syncStatus === "error"
                    ? "#d32f2f"
                    : syncStatus === "offline"
                    ? "#787585"
                    : "#1535BD",
                fontFamily: "Mulish, sans-serif",
                letterSpacing: "0.02em",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {syncStatus === "syncing" && (
                <CircularProgress
                  size={8}
                  thickness={5}
                  style={{ color: "inherit", marginRight: 3 }}
                />
              )}
              {syncStatus === "error"
                ? "Sync failed"
                : syncStatus === "offline"
                ? "Offline"
                : "Syncing..."}
            </Box>
          )}
          {userData ? (
            <Tooltip title={userData.name || userData.username}>
              <Avatar
                src={userData.picture}
                alt={userData.name || userData.username}
                sx={{
                  width: 34,
                  height: 34,
                  cursor: "pointer",
                  boxShadow: `0 0 0 2px #F1F3F4, 0 0 0 4px #1535BD`,
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 0.75 },
                }}
                onClick={async () => {
                  const { isConfirmed } = await Swal.fire({
                    title: "Sign out?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Sign out",
                    cancelButtonText: "Cancel",
                    confirmButtonColor: "#d33",
                    reverseButtons: true,
                  });
                  if (isConfirmed) handleLogout();
                }}
              />
            </Tooltip>
          ) : !settings?.hideSignIn ? (
            <Button
              variant="contained"
              onClick={handleLogin}
              startIcon={<GoogleIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                backgroundColor: "#4285F4",
                color: "white",
                textTransform: "none",
                borderRadius: "20px",
                fontSize: "0.8rem",
                whiteSpace: "nowrap",
                px: 2,
                py: 0.65,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#3367D6", boxShadow: "none" },
              }}
            >
              Sign in
            </Button>
          ) : null}
        </Box>
      </Box>

      {/* Bottom-sheet menu (home / mobile only). Swipeable so the drag handle works. Not
          kept mounted: a hidden .MuiModal-root reads as an open dialog to the hotkeys. */}
      <SwipeableDrawer
        anchor="bottom"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpen={() => {}}
        disableSwipeToOpen
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            px: 1,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
          },
        }}
      >
        {/* Drag handle */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: "rgba(0,0,0,0.13)" }} />
        </Box>

        <List disablePadding sx={{ pb: 1 }}>
          <ListItemButton
            sx={{ borderRadius: 2, py: 1.5, px: 2 }}
            onClick={() => {
              setMenuOpen(false);
              onWalkthroughClick?.();
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SchoolOutlinedIcon sx={{ color: primary }} />
            </ListItemIcon>
            <ListItemText
              primary="Guided Tour"
              primaryTypographyProps={{ fontWeight: 600, fontFamily: "Mulish, sans-serif" }}
            />
          </ListItemButton>

          <ListItemButton
            sx={{ borderRadius: 2, py: 1.5, px: 2 }}
            onClick={() => {
              setMenuOpen(false);
              setFeedbackOpen(true);
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <RateReviewOutlinedIcon sx={{ color: primary }} />
            </ListItemIcon>
            <ListItemText
              primary="Submit Feedback"
              primaryTypographyProps={{ fontWeight: 600, fontFamily: "Mulish, sans-serif" }}
            />
          </ListItemButton>

          <ListItemButton
            sx={{ borderRadius: 2, py: 1.5, px: 2 }}
            onClick={() => {
              setMenuOpen(false);
              history.push("/privacy-policy");
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PolicyOutlinedIcon sx={{ color: primary }} />
            </ListItemIcon>
            <ListItemText
              primary="Privacy Policy"
              primaryTypographyProps={{ fontWeight: 600, fontFamily: "Mulish, sans-serif" }}
            />
          </ListItemButton>

          <ListItemButton
            sx={{ borderRadius: 2, py: 1.5, px: 2 }}
            onClick={() => {
              setMenuOpen(false);
              history.push("/settings");
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsOutlinedIcon sx={{ color: primary }} />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{ fontWeight: 600, fontFamily: "Mulish, sans-serif" }}
            />
          </ListItemButton>
        </List>
      </SwipeableDrawer>

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        mode="general"
      />
    </>
  );
};

export default PageHeader;
